import { inject } from '@angular/core';
import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
  HttpResponse,
  HttpBackend,
  HttpClient,
} from '@angular/common/http';
import {
  BehaviorSubject,
  catchError,
  Observable,
  switchMap,
  throwError,
  filter,
  take,
  mergeMap,
  of,
  map,
  tap,
  share,
  finalize
} from 'rxjs';
import { IAuthAccessToken, ELocalStorageKeys } from '@account/data-access';
import { AuthStore } from '@auth/data-access';
import { APP_CONFIG, AppConfig, AppSource } from '@application/config';
import { LocalStorageFacade } from '@application/localstorage';

/**
 * Marks a request that is being replayed after a token refresh. Downstream
 * interceptors (e.g. the Sentry reporter) use it to tell the first 401 — the one
 * that triggers the refresh — apart from a 401 that persists *after* the token
 * was refreshed and the request retried, which is the only one worth reporting.
 */
export const IS_TOKEN_REFRESH_RETRY = new HttpContextToken<boolean>(() => false);

let isRefreshing = false;
/**
 * O `/Auth/Refresh` em curso, partilhado por todos os que precisam de um token
 * novo enquanto ele corre. O `TokenId` é rodado a cada refresh: um segundo
 * pedido com o mesmo `TokenId` chega já consumido, o backend responde 400 e a
 * sessão cai no `logout()`. Por isso só pode haver um refresh de cada vez.
 */
let refreshInFlight: Observable<IAuthAccessToken> | null = null;
const refreshTokenSubject: BehaviorSubject<IAuthAccessToken | null> = new BehaviorSubject<IAuthAccessToken | null>(null);
let authData: IAuthAccessToken | null = null;
const resolveAuthStore = () => inject(AuthStore);
type AuthStoreInstance = ReturnType<typeof resolveAuthStore>;
let authStoreInstance: AuthStoreInstance | null = null;
export const TokenInterceptor: HttpInterceptorFn = (
  request: HttpRequest<any>,
  next: HttpHandlerFn,
): Observable<HttpEvent<any>> => {
  const appConfig = inject<AppConfig>(APP_CONFIG);
  const httpBackend = inject(HttpBackend);
  const httpNoInterceptors = new HttpClient(httpBackend);
  const localStorageFacade = inject(LocalStorageFacade);

  const getAuthStore = (): AuthStoreInstance | null => {
    if (authStoreInstance) {
      return authStoreInstance;
    }

    try {
      authStoreInstance = resolveAuthStore();
    } catch {
      authStoreInstance = null;
    }

    return authStoreInstance;
  };

  const readAuth = (): IAuthAccessToken | null => {
    const store = getAuthStore();
    const storeAuth = store?.getAuthData();
    if (storeAuth) {
      return storeAuth;
    }

    try {
      const prefixed = localStorageFacade.getItem(ELocalStorageKeys.auth);
      const rawPrefixed = prefixed ?? localStorageFacade.getItemRaw(`actuar/${ELocalStorageKeys.auth}`);
      const raw = rawPrefixed ?? localStorageFacade.getItemRaw(ELocalStorageKeys.auth);
      return raw ? (JSON.parse(raw) as IAuthAccessToken) : null;
    } catch {
      return null;
    }
  };

  const writeAuth = (data: IAuthAccessToken) => {
    getAuthStore()?.setAuthData(data);
    try {
      localStorageFacade.setItem(ELocalStorageKeys.auth, JSON.stringify(data));
    } catch {
      // noop
    }
  };

  // `HttpContext` is mutable and shared by reference across clones, so setting the
  // marker in place is enough — `addAuthToken` clones the request afterwards and
  // the same context (with the marker) carries over. No extra clone needed here.
  const markRefreshRetry = (req: HttpRequest<any>): HttpRequest<any> => {
    req.context.set(IS_TOKEN_REFRESH_RETRY, true);
    return req;
  };

  const addAuthToken = (req: HttpRequest<any>): HttpRequest<any> => {
    if (!req.withCredentials) {
      return req;
    }

    authData = readAuth();
    if (!authData?.Token) {
      return req.clone({ withCredentials: false });
    }

    let headers = req.headers.set('Authorization', `Bearer ${authData.Token}`);

    // JSON é o padrão, não uma imposição. Duas exceções legítimas: um `FormData`
    // precisa que o browser gere o `boundary`, e um corpo binário declara o seu
    // próprio tipo — o vídeo do Link na Bio segue como `Blob` cru justamente para o
    // Worker o passar em streaming ao bucket, e sobrescrever aqui apagava esse tipo
    // e fazia o servidor recusar o ficheiro. Só se preenche o que ninguém declarou.
    if (!(req.body instanceof FormData) && !req.headers.has('Content-Type')) {
      headers = headers.set('Content-Type', 'application/json');
    }

    return req.clone({
      withCredentials: false,
      headers
    });
  };

  const waitForRefreshToken = refreshTokenSubject.pipe(
    filter((authData) => authData !== null),
    take(1),
    switchMap(() => next(addAuthToken(markRefreshRetry(request)))),
    catchError((error) => {
      if (isRefreshing) {
        refreshTokenSubject.next(null);
        isRefreshing = false;
      }
      return throwError(() => error);
    })
  );

  const refreshAuth = (): Observable<IAuthAccessToken> => {
    if (refreshInFlight) {
      return refreshInFlight;
    }

    const current = readAuth();
    if (!current?.TokenId) {
      return throwError(() => new HttpErrorResponse({
        status: 401,
        statusText: 'Unauthorized - User not logged in'
      }));
    }

    isRefreshing = true;
    refreshTokenSubject.next(null);

    const body = {
      TokenId: current.TokenId,
      AppSource: AppSource[appConfig.appSource],
    } as any;

    // Só limpa o estado se ainda for este o refresh em curso: o `finalize` corre
    // depois do `next`, quando outro refresh já pode ter começado.
    const settle = () => {
      if (refreshInFlight !== inFlight) return;
      isRefreshing = false;
      refreshInFlight = null;
    };

    const inFlight: Observable<IAuthAccessToken> = httpNoInterceptors
      .post<IAuthAccessToken>(
        appConfig.apiUrl.userService + '/Auth/Refresh',
        body
      )
      .pipe(
        tap({
          next: (newAuth) => {
            settle();
            writeAuth(newAuth);
            authData = newAuth;
            refreshTokenSubject.next(newAuth);
          },
          error: (error) => {
            settle();
            refreshTokenSubject.next(null);

            if (error.status === 400) {
              getAuthStore()?.logout();
            }
          },
        }),
        // Cancelado por todos os interessados antes de responder: o próximo
        // pedido tem de poder começar outro refresh.
        finalize(settle),
        share()
      );

    refreshInFlight = inFlight;
    return inFlight;
  };

  const handle401Error = (
    req: HttpRequest<any>,
    next: HttpHandlerFn,
  ): Observable<HttpEvent<any>> => {
    if (!isRefreshing) {
      return refreshAuth().pipe(
        switchMap(() => next(addAuthToken(markRefreshRetry(req))))
      );
    }

    return waitForRefreshToken
  };

  // `AuthStore.refreshToken()` — chamado pelas grades OData a cada 401 — também
  // passa por aqui. Seguir o caminho normal punha-o a correr ao lado do refresh
  // do interceptor (ou, durante um, a ser reenviado depois com o `TokenId` que
  // esse refresh acabou de consumir). Ele junta-se ao refresh partilhado e
  // recebe o mesmo token novo.
  if (request.url.endsWith('/Auth/Refresh')) {
    return refreshAuth().pipe(
      map((newAuth) => new HttpResponse<IAuthAccessToken>({
        status: 200,
        body: newAuth,
        url: request.url,
      }))
    );
  }

  const authenticatedRequest = addAuthToken(request);

  return of(isRefreshing).pipe(
    switchMap((isRefreshing) => {
      if (!isRefreshing) {
        return next(authenticatedRequest);
      }
      return waitForRefreshToken;
    }),
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 && request.withCredentials) {
        return handle401Error(request, next);
      }
      return throwError(() => error);
    })
  );
};
